"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useFocusTrap } from "@/lib/useFocusTrap";

export interface HelpVideo {
  id: string;
  title: string;
  description: string;
  src: string;
  poster: string;
  durationLabel: string;
}

// Quick-tour catalog. The mp4s live in public/videos/ and are excluded from
// the PWA install-time precache (next.config.js publicExcludes) — bytes only
// load when the user opens a tour, and the service worker's mp4 CacheFirst
// route keeps them available offline after first play.
export const HELP_VIDEOS: HelpVideo[] = [
  {
    id: "capture",
    title: "Capture a thought",
    description:
      "Drop whatever is on your mind into Quick Capture — DecisionEase sorts it for you.",
    src: "/videos/capture-tour.mp4",
    poster: "/videos/capture-tour-poster.jpg",
    durationLabel: "20s",
  },
  {
    id: "schedule",
    title: "Plan your day with AI Time Box",
    description:
      "Let AI place your goals and tasks into today's open windows, then save the plan.",
    src: "/videos/schedule-tour.mp4",
    poster: "/videos/schedule-tour-poster.jpg",
    durationLabel: "24s",
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function HelpVideosOverlay({ open, onClose }: Props) {
  const [activeVideo, setActiveVideo] = useState<HelpVideo | null>(null);
  const reduceMotion = useReducedMotion();
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Every (re)open starts on the menu, never mid-player.
  useEffect(() => {
    if (open) setActiveVideo(null);
  }, [open]);

  // Tab trap + initial focus + Esc + focus-restore-on-close, via the same
  // hook ChatSheet/SettingsSheet use.
  useFocusTrap(open, cardRef, { onEscape: onClose });

  // Body scroll locks while the overlay is open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const closeButton = (
    <button
      onClick={onClose}
      data-testid="help-close-btn"
      aria-label="Close quick tours"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted hover:text-text hover:bg-secondary/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med"
    >
      <svg
        aria-hidden="true"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-videos-title"
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
        data-testid="help-overlay-backdrop"
      />
      <motion.div
        ref={cardRef}
        initial={reduceMotion ? false : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: reduceMotion ? 0 : 0.25, ease: "easeOut" }}
        className={`relative w-full ${
          activeVideo ? "max-w-3xl" : "max-w-md"
        } max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-lg`}
      >
        {activeVideo ? (
          <div className="p-5">
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={() => setActiveVideo(null)}
                aria-label="Back to tours"
                className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-text-light hover:text-text hover:bg-secondary/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med"
              >
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>
              <h2
                id="help-videos-title"
                className="min-w-0 truncate text-sm font-semibold text-text"
              >
                {activeVideo.title}
              </h2>
              {closeButton}
            </div>
            {/* preload="none" + mount-on-demand keep video bytes off the wire
                until the user actually picks a tour. */}
            <video
              key={activeVideo.id}
              src={activeVideo.src}
              poster={activeVideo.poster}
              controls
              playsInline
              autoPlay
              preload="none"
              aria-label={activeVideo.title}
              className="mt-4 aspect-video w-full rounded-xl bg-black"
            />
            <p className="mt-3 text-xs text-text-light">
              {activeVideo.description}
            </p>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="help-videos-title" className="text-lg font-semibold text-text">
                  Quick tours
                </h2>
                <p className="mt-1 text-sm text-text-light">
                  Two short videos showing how DecisionEase works.
                </p>
              </div>
              {closeButton}
            </div>
            <div className="mt-4 space-y-3">
              {HELP_VIDEOS.map((video, index) => (
                <button
                  key={video.id}
                  data-testid={`help-video-${video.id}`}
                  onClick={() => setActiveVideo(video)}
                  className="flex w-full items-center gap-4 rounded-xl border border-border bg-secondary/30 p-3 text-left transition-all hover:border-forest-med hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med"
                >
                  <div className="relative w-28 shrink-0 overflow-hidden rounded-lg">
                    {/* Poster thumbnails are tiny jpgs; plain <img> keeps the
                        overlay free of next/image layout requirements. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={video.poster}
                      alt=""
                      className="aspect-video w-full object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-forest/85 text-white shadow-md">
                        <svg
                          aria-hidden="true"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-text">
                      {index + 1}. {video.title}
                    </p>
                    <p className="mt-0.5 text-xs text-text-light">
                      {video.description}
                    </p>
                    <span className="mt-1.5 inline-block rounded-full bg-forest/10 px-2 py-0.5 text-[0.68rem] font-medium text-forest">
                      {video.durationLabel}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
